import { serverCache } from '~/server/utils/cache';
import { musicService, type MusicListResponse, type MusicRank, type MusicCategory } from '~/services/musicService';
import { useI18n } from 'vue-i18n';
import { localeMappings, type SupportedLocale } from '~/i18n/config';
import { readPublicMusicList } from '~/utils/public-music-list.mjs';

export function useMusicCache() {
  const { locale } = useI18n();
  // Allow the same two bounded read attempts as the homepage rankings. The old
  // 2s cache race could return an empty SSR section while its request still ran.
  const ssrTimeoutMs = import.meta.server ? 14000 : 0;
  const requestTimeout = import.meta.server ? { timeout: 6500 } : {};
  
  const getFeaturedMusic = async () => {
    const key = `featuredMusic-${locale.value}`;
    const language = localeMappings.shortToFull[locale.value as SupportedLocale] || 'en_US';
    
    // 使用服务端缓存，ttl设置为1小时
    return await serverCache.get(key, async () => {
      console.log(`[Cache] Getting fresh featured music data for ${key}`);
      const response = await musicService.getFeaturedMusic(undefined, { ...requestTimeout, headers: { 'x-language': language } });
      readPublicMusicList(response);
      return response;
    }, 3600, ssrTimeoutMs);
  };
  
  const getCategoryList = async () => {
    const key = `categoryList-${locale.value}`;
    const language = localeMappings.shortToFull[locale.value as SupportedLocale] || 'en_US';
    
    return await serverCache.get(key, async () => {
      console.log(`[Cache] Getting fresh category data for ${key}`);
      const response = await musicService.getCategoryList(undefined, { ...requestTimeout, headers: { 'x-language': language } });
      readPublicMusicList(response);
      return response;
    }, 3600, ssrTimeoutMs);
  };
  
  const refreshFeaturedMusic = () => {
    serverCache.invalidateAll('featuredMusic-');
  };
  
  const refreshCategoryList = () => {
    serverCache.invalidateAll('categoryList-');
  };
  
  // 刷新某一语言的所有音乐数据
  const refreshLanguageData = (lang: string) => {
    serverCache.invalidate(`featuredMusic-${lang}`);
    serverCache.invalidate(`categoryList-${lang}`);
  };
  
  return {
    getFeaturedMusic,
    getCategoryList,
    refreshFeaturedMusic,
    refreshCategoryList,
    refreshLanguageData
  };
}
